<template>
  <div class="h-100 tl-page">
    <div class="page-header flex-row flex-wrap gap-2">
      <div class="d-none d-md-block title">Анализ таймлайна (~13ч)</div>
      <div class="tl-toolbar flex-row m-auto">
        <CInputGroup size="sm" class="tl-toolbar-group">
          <CFormInput
            type="text"
            v-model.trim="search"
            autocomplete="off"
            placeholder="Поиск по серверам, камерам"
          />
          <span class="threshold-label">Порог (ч):</span>
          <input
            type="number"
            v-model.number="minHours"
            min="0"
            max="24"
            step="0.5"
            class="threshold-input"
          />
          <span class="threshold-sep">—</span>
          <input
            type="number"
            v-model.number="maxHours"
            min="0"
            max="24"
            step="0.5"
            class="threshold-input"
          />
          <CButton color="primary" @click="startScan" :disabled="isScanning">
            {{ isScanning ? `${scannedCount} / ${totalCamerasCount} камер...` : 'Сканировать' }}
          </CButton>
        </CInputGroup>
        <div class="tl-meta">
          {{ qualifiedDivisions.length }} серверов (v &gt; 2.1.0)
          <span v-if="totalFlagged > 0" class="meta-flags">· {{ totalFlagged }} аномалий</span>
        </div>
      </div>
    </div>

    <div class="tl-content">
      <div v-if="!hasResults && !isScanning" class="empty-state">
        <div class="empty-icon">📡</div>
        <div class="empty-title">Нет данных для отображения</div>
        <div class="empty-sub">
          Нажмите «Сканировать» для анализа таймлайнов на серверах с версией &gt; 2.1.0.<br />
          Будут выделены дни, где запись длилась от {{ minHours }}ч до {{ maxHours }}ч.
        </div>
        <div v-if="qualifiedDivisions.length === 0" class="empty-warn">
          Серверов с версией &gt; 2.1.0 не найдено.
        </div>
      </div>

      <div class="divisions-list">
        <div
          v-for="div in filteredDivisions"
          :key="div.guid"
          class="division-block"
          :class="{
            'has-flags': getDivState(div).flaggedCount > 0,
            'is-ok': getDivState(div).status === 'done' && getDivState(div).flaggedCount === 0
          }"
        >
          <div class="division-header" @click="toggleDiv(div.guid)">
            <span class="chevron">{{ expandedGuids.has(div.guid) ? '▼' : '▶' }}</span>
            <span class="div-name">{{ div.name }}</span>
            <span class="div-ver">v{{ div.version }}</span>
            <span class="spacer" />
            <span
              class="badge loading-badge"
              v-if="getDivState(div).status === 'loading'"
            >
              {{ getDivState(div).processedCams }}/{{ getDivState(div).totalCams }}
            </span>
            <span
              class="badge error-badge"
              v-else-if="getDivState(div).status === 'error'"
              :title="getDivState(div).errorMsg"
            >Ошибка</span>
            <span
              class="badge ok-badge"
              v-else-if="getDivState(div).status === 'done' && getDivState(div).flaggedCount === 0"
            >Норма</span>
            <span
              class="badge flag-badge"
              v-else-if="getDivState(div).flaggedCount > 0"
            >⚠ {{ getDivState(div).flaggedCount }} аномал.</span>
          </div>

          <div
            v-if="expandedGuids.has(div.guid) && getDivState(div).flaggedItems.length > 0"
            class="cameras-list"
          >
            <div
              v-for="item in getDivState(div).flaggedItems"
              :key="item.camId"
              class="cam-row"
            >
              <span class="cam-id">{{ item.camId }}</span>
              <div class="day-tags">
                <span
                  v-for="day in item.days"
                  :key="day.date"
                  class="day-tag"
                  :title="`Спан: ${day.spanFormatted} | Запись: ${day.totalFormatted}`"
                >
                  {{ day.date }}
                  <span class="span-val">{{ day.spanFormatted }}</span>
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="expandedGuids.has(div.guid) && getDivState(div).status === 'done' && getDivState(div).flaggedItems.length === 0"
            class="no-flags-msg"
          >
            Аномальных дней не обнаружено
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useDefaultStore } from '@/store'
import { CButton, CFormInput, CInputGroup } from '@coreui/bootstrap-vue'

export default {
  name: 'TimelineStatusPage',
  components: { CButton, CFormInput, CInputGroup },

  setup() {
    const store = useDefaultStore()
    return { store }
  },

  data() {
    return {
      search: '',
      minHours: 12,
      maxHours: 14,
      isScanning: false,
      scannedCount: 0,
      divisionStates: {},
      expandedGuids: new Set(),
    }
  },

  mounted() {
    this.store.setPageTitle('Анализ таймлайна (~13ч)')
  },

  computed: {
    allDivisions() {
      return this.store.allDivisions || []
    },
    qualifiedDivisions() {
      return this.allDivisions.filter(d => this.isVersionGt210(d.version))
    },
    totalCamerasCount() {
      return this.qualifiedDivisions.reduce((sum, d) => sum + (d.camList?.length || 0), 0)
    },
    hasResults() {
      return Object.keys(this.divisionStates).length > 0
    },
    totalFlagged() {
      return Object.values(this.divisionStates)
        .reduce((sum, s) => sum + (s.flaggedCount || 0), 0)
    },
    filteredDivisions() {
      const search = this.search.toLowerCase()
      return this.qualifiedDivisions
        .filter(d => this.divisionStates[d.guid])
        .filter(d => {
          if (!search) return true
          const state = this.divisionStates[d.guid]
          return (
            d.name?.toLowerCase().includes(search) ||
            d.url?.toLowerCase().includes(search) ||
            d.address?.toLowerCase().includes(search) ||
            state?.flaggedItems?.some(item => item.camId.toLowerCase().includes(search))
          )
        })
        .sort((a, b) => {
          const fa = this.divisionStates[a.guid]?.flaggedCount || 0
          const fb = this.divisionStates[b.guid]?.flaggedCount || 0
          if (fb !== fa) return fb - fa
          return (a.name || '').localeCompare(b.name || '', 'ru')
        })
    }
  },

  methods: {
    isVersionGt210(version) {
      if (!version) return false
      const parts = version.split('.').map(Number)
      const ref = [2, 1, 0]
      for (let i = 0; i < 3; i++) {
        const a = parts[i] ?? 0
        const b = ref[i]
        if (a > b) return true
        if (a < b) return false
      }
      return false
    },

    getDivBaseUrl(division) {
      return window.location.protocol === 'http:'
        ? `http://${division.address}`
        : `https://${division.url}`
    },

    getDivState(division) {
      return this.divisionStates[division.guid] || {
        status: 'idle',
        flaggedCount: 0,
        flaggedItems: [],
        processedCams: 0,
        totalCams: 0,
        errorMsg: null
      }
    },

    toggleDiv(guid) {
      const next = new Set(this.expandedGuids)
      if (next.has(guid)) next.delete(guid)
      else next.add(guid)
      this.expandedGuids = next
    },

    formatDuration(ms) {
      if (ms == null || isNaN(ms)) return '?'
      const totalSec = Math.floor(ms / 1000)
      const h = Math.floor(totalSec / 3600)
      const m = Math.floor((totalSec % 3600) / 60)
      return `${h}ч ${m.toString().padStart(2, '0')}м`
    },

    isInRange(ms) {
      const hours = ms / 3600000
      return hours >= this.minHours && hours <= this.maxHours
    },

    analyzeDays(days) {
      const flagged = []
      for (const day of days) {
        const segments = day.segments || []
        if (!segments.length) continue

        const startMs = Math.min(...segments.map(s => s.startMs))
        const endMs = Math.max(...segments.map(s => s.endMs))
        const spanMs = endMs - startMs
        const totalMs = segments.reduce((sum, s) => sum + (s.endMs - s.startMs), 0)

        if (this.isInRange(spanMs)) {
          flagged.push({
            date: day.date || day.day || '?',
            spanMs,
            totalMs,
            spanFormatted: this.formatDuration(spanMs),
            totalFormatted: this.formatDuration(totalMs),
          })
        }
      }
      return flagged
    },

    async fetchCameraTimeline(baseUrl, camId) {
      const res = await fetch(`${baseUrl}/api/timeline/${encodeURIComponent(camId)}/all`)
      if (!res.ok) return null
      return await res.json()
    },

    setDivState(guid, patch) {
      const current = this.divisionStates[guid] || {
        status: 'idle',
        flaggedCount: 0,
        flaggedItems: [],
        processedCams: 0,
        totalCams: 0,
        errorMsg: null
      }
      this.divisionStates = {
        ...this.divisionStates,
        [guid]: { ...current, ...patch }
      }
    },

    async scanDivision(division) {
      const cameras = division.camList || []
      const baseUrl = this.getDivBaseUrl(division)

      this.setDivState(division.guid, {
        status: 'loading',
        totalCams: cameras.length,
        processedCams: 0,
        flaggedCount: 0,
        flaggedItems: [],
        errorMsg: null,
      })

      const BATCH = 5
      const flaggedItems = []

      try {
        for (let i = 0; i < cameras.length; i += BATCH) {
          const batch = cameras.slice(i, i + BATCH)
          const results = await Promise.allSettled(
            batch.map(camId => this.fetchCameraTimeline(baseUrl, camId))
          )

          results.forEach((result, idx) => {
            const camId = batch[idx]
            if (result.status === 'fulfilled' && result.value?.days) {
              const flaggedDays = this.analyzeDays(result.value.days)
              if (flaggedDays.length > 0) {
                flaggedItems.push({ camId, days: flaggedDays })
              }
            }
          })

          this.setDivState(division.guid, {
            processedCams: Math.min(i + BATCH, cameras.length),
            flaggedItems: [...flaggedItems],
            flaggedCount: flaggedItems.reduce((sum, item) => sum + item.days.length, 0),
          })

          this.scannedCount += batch.length
        }

        this.setDivState(division.guid, {
          status: 'done',
          processedCams: cameras.length,
          flaggedItems,
          flaggedCount: flaggedItems.reduce((sum, item) => sum + item.days.length, 0),
        })

        if (flaggedItems.length > 0 && !this.expandedGuids.has(division.guid)) {
          this.toggleDiv(division.guid)
        }
      } catch (err) {
        this.setDivState(division.guid, {
          status: 'error',
          errorMsg: err.message || 'Неизвестная ошибка',
        })
      }
    },

    async startScan() {
      if (this.isScanning) return
      this.isScanning = true
      this.scannedCount = 0
      this.divisionStates = {}
      this.expandedGuids = new Set()

      const CONCURRENT = 3
      const divisions = this.qualifiedDivisions

      for (let i = 0; i < divisions.length; i += CONCURRENT) {
        const batch = divisions.slice(i, i + CONCURRENT)
        await Promise.allSettled(batch.map(div => this.scanDivision(div)))
      }

      this.isScanning = false
    }
  }
}
</script>

<style scoped>
.tl-page {
  display: flex;
  flex-direction: column;
}

.tl-toolbar {
  min-width: min(100%, 760px);
  gap: 0.5rem;
  flex-direction: column;
}

.tl-toolbar-group {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0;
}

.threshold-label {
  font-size: 12px;
  color: #aaa;
  white-space: nowrap;
  padding: 0 8px;
  background: #1a1d23;
  border: 1px solid #444;
  border-left: none;
  display: flex;
  align-items: center;
}

.threshold-sep {
  background: #1a1d23;
  border: 1px solid #444;
  border-left: none;
  border-right: none;
  padding: 0 4px;
  display: flex;
  align-items: center;
  color: #888;
}

.threshold-input {
  width: 60px;
  background: #1a1d23;
  border: 1px solid #444;
  color: #e0e0e0;
  padding: 4px 8px;
  font-size: 13px;
  text-align: center;
  outline: none;
}

.threshold-input:first-of-type {
  border-left: none;
}

.threshold-input:last-of-type {
  border-right: none;
}

.threshold-input:focus {
  border-color: #4a9eff;
}

.tl-meta {
  font-size: 12px;
  color: #7b8794;
  text-align: right;
}

.meta-flags {
  color: #ff9800;
  font-weight: 600;
}

.tl-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 0.75rem;
  color: #7b8794;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  line-height: 1;
}

.empty-title {
  font-size: 16px;
  color: #aaa;
  font-weight: 500;
}

.empty-sub {
  font-size: 13px;
  max-width: 420px;
  line-height: 1.5;
}

.empty-warn {
  font-size: 13px;
  color: #f44336;
  margin-top: 0.5rem;
}

.divisions-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.division-block {
  background: #1e2228;
  border-radius: 8px;
  border: 1px solid #2a2f38;
  overflow: hidden;
  transition: border-color 0.2s;
}

.division-block.has-flags {
  border-color: #7a4a00;
}

.division-block.is-ok {
  border-color: #1a3d1a;
}

.division-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.division-header:hover {
  background: rgba(255, 255, 255, 0.04);
}

.chevron {
  font-size: 10px;
  color: #666;
  width: 12px;
  flex-shrink: 0;
}

.div-name {
  font-size: 14px;
  font-weight: 600;
  color: #e0e0e0;
}

.div-ver {
  font-size: 11px;
  color: #666;
}

.spacer {
  flex: 1;
}

.badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 3px;
  white-space: nowrap;
}

.loading-badge {
  background: #2a3040;
  color: #7b9eff;
}

.error-badge {
  background: #4d1b1b;
  color: #f44336;
  cursor: help;
}

.ok-badge {
  background: #1b3a1b;
  color: #4caf50;
}

.flag-badge {
  background: #4d3000;
  color: #ff9800;
  font-weight: 600;
}

.cameras-list {
  border-top: 1px solid #2a2f38;
  padding: 8px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cam-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #1a1d22;
}

.cam-row:last-child {
  border-bottom: none;
}

.cam-id {
  font-size: 11px;
  color: #aaa;
  font-family: monospace;
  min-width: 180px;
  flex-shrink: 0;
  padding-top: 2px;
}

.day-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.day-tag {
  font-size: 10px;
  background: #2a2000;
  border: 1px solid #5a3800;
  color: #ffb74d;
  border-radius: 4px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: default;
}

.span-val {
  font-weight: 600;
  color: #ff9800;
}

.no-flags-msg {
  border-top: 1px solid #2a2f38;
  padding: 8px 14px;
  font-size: 11px;
  color: #4caf50;
  font-style: italic;
}

@media (max-width: 767px) {
  .tl-content {
    padding: 1rem;
  }

  .cam-id {
    min-width: 120px;
  }

  .tl-meta {
    text-align: left;
  }
}
</style>
